<template>
  <div class="handover-schedule-edit">
    <Sticky
      class="stick-btnGroup"
      extendStickyClass="stickyBtnClass"
      :fullWidth="false"
      :align="'right'"
    >
      <div class="btnGroup">
        <button
          v-if="!isEditing && allowGeneratePdf"
          class="btn--darkblue generatePDF"
          @click="generatePDF"
        >{{ $t('action.generatePDF') }}</button>
        <button
          v-if="!isEditing && allowEdit"
          class="btn--orange edit"
          @click="triggerEditStatus"
        >{{ $t('action.edit') }}</button>
        <button
          v-if="isEditing && allowEdit"
          class="btn--darkblue cancel"
          @click="cancel"
        >{{ $t('action.cancel') }}</button>
        <button
          v-if="isEditing && allowEdit"
          class="btn--orange save"
          @click="save"
        >{{ $t('action.save') }}</button>
        <button
          v-if="isEditing && allowEdit && isHandover"
          class="btn--orange importTemplate"
          @click="openModal"
        >{{ $t('handover.import') }}</button>
        <button
          v-if="isEditing && allowEdit"
          class="btn--orange add"
          @click="add"
        >{{ $t('action.add') }}</button>
      </div>
    </Sticky>
    <div class="des-header">
      <div class="condition">
        <div class="condition-title">{{ $t(`terms.${regionName}.hotb.conditionOfPremises`) }}</div>
        <i class="des">{{ $t(`terms.${regionName}.hotb.conditionOfPremisesDes.${hotbType}`) }}</i>
      </div>
    </div>
    <UploadPhotoArea
      v-if="isHandover"
      :uploadSuccess="uploadSuccess"
      :photoesInfo="schedulesList"
      :selectEditPhotoInfo="selectEditPhotoInfo"
      :isEditing="isEditing"
      :allowEdit="allowEdit"
      :updatePhotoInfo="updatePhotoInfo"
      :deleteOnePhoto="deleteOnePhoto"
      :beforeUpload="beforeUpload"
      :deleteOnePhotoInfo="deleteOnePhotoInfo"
      :onDragEnd="dragEnd"
    />
    <div
      v-else
      class="takeback-schedule"
    >
      <div
        class="inputGroupItem"
        v-for="(item, index) in schedulesList"
        :key="index"
      >
        <div class="facility">
          <span class="index">{{ index + 1 }}. </span>
          <InputField
            class="facility-input"
            :readonly="!isEditing"
            :showColon="false"
            :value="item.facility || ''"
            @updateValue="val => updatePhotoInfo('facility', index, val)"
            required
          />
          <i
            class="fa fa-times"
            v-if="isEditing"
            @click="deleteOnePhotoInfo(index)"
          />
        </div>
        <TextField
          class="description"
          :readonly="!isEditing"
          :showColon="false"
          :value="item.description ? item.description : !isEditing ? 'N/A' : ''"
          @updateValue="val => updatePhotoInfo('description', index, val)"
        />
      </div>
    </div>
    <RemarksList
      ref="remarksList"
      :allowEdit="allowEdit"
    />
    <HotbScheduleSignature
      :editStatus="isEditing"
      ref="signatureRef"
      :transactionTime="transactionTime"
    />
    <div
      class="empty-schedule"
      v-if="!schedulesList.length"
    >
      {{ isEditing ? $t('handoverTakebackSchedule.addNewSchedule') : $t('handoverTakebackSchedule.noSchedule') }}
    </div>
    <Modal
      ref="chooseTemplatesModal"
      :modalId="modalId"
      class="choose-template-modal"
      :showBtns="true"
    >
      <p
        slot="modal-header"
        class="font-bold"
      >{{ $t('hint.chooseTemplate') }}</p>
      <div slot="content">
        <HKLSelect
          :value="choosedTemplate || undefined"
          :options="templateHklOptions"
          placeholder=""
          @change="chooseOneTemplate"
        />
      </div>
      <div slot="btns">
        <button
          class="btn btn--darkblue"
          @click="closeModal"
        >{{ $t('action.cancel') }}</button>
        <button
          class="btn btn--orange"
          @click="importTemplate"
        >{{ $t('action.ok') }}</button>
      </div>
    </Modal>
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapActions, mapGetters, mapMutations } from 'vuex'

import DialogSlot from '@/controls/DialogSlot'
import InputField from '@/controls/InputField'
import Modal from '@/controls/Modal'
import HKLSelect from '@/controls/Select/HKLSelect.vue'
import Sticky from '@/controls/Sticky'
import TextField from '@/controls/TextField'
import UploadPhotoArea from '@/controls/UploadPhotoArea'
import draggable from 'vuedraggable'
import HotbScheduleSignature from './HotbScheduleSignature'
import RemarksList from './RemarksList'

import { getBuildingInfoById, getBuildingTemplateDetail } from '@/api/buildings/request'
import { getKey } from '@/api/common/request'
import { exportHotbSchedule } from '@/api/printers/request'
import { fusionInputCheckUtil } from '@/plugin/FusionInputCheck'
import { utils as formUtils } from '@/plugin/InputChange'
import mutationTypes from '@/store/mutation-types'
import arrayHelper from '@/utils/arrayHelper'
import { templateChip } from '@/utils/consts'
import dateUtil from '@/utils/dateUtil'
import { hotbPremiseBuildingType } from '@/utils/locale'
import notification from '@/utils/notification'

export default {
  name: 'HandoverTakebackSchedule',
  props: {
    allowEdit: {
      type: Boolean,
      default: false,
    },
    allowGeneratePdf: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    const modalId = `choose-template-${new Date().getTime()}-${Math.floor(Math.random() * 1000)}`
    return {
      modalId,
      editItem: {},
      sourceId: '',
      isEditing: false,
      editIndex: -1,
      showEditRemark: false,
      pdfId: null,
      templatesOption: [],
      choosedTemplate: '',
      buildingInfo: [],
    }
  },
  components: {
    HotbScheduleSignature,
    Sticky,
    InputField,
    UploadPhotoArea,
    TextField,
    DialogSlot,
    draggable,
    RemarksList,
    Modal,
    HKLSelect,
  },
  computed: {
    ...mapGetters('hotb', ['currentHotb', 'isHandover']),
    ...mapGetters('regions', ['regionName']),
    hotbType () {
      return this.currentHotb.type
    },
    schedulesList () {
      return lodash.cloneDeep(lodash.get(this.currentHotb, 'schedules', []))
    },
    isAsIs () {
      return this.currentHotb && lodash.get(this.currentHotb, 'premiseBuildingType', '') === hotbPremiseBuildingType.asIs
    },
    remarksListAsIsRadio () {
      return lodash.get(this.currentHotb, 'remarksListAsIsRadio', '')
    },
    otherRemarksRadio () {
      return lodash.get(this.currentHotb, 'otherRemarksRadio', '')
    },
    transactionTime () {
      const time = dateUtil.format(this.currentHotb.scheduledTransactionDate, 'HH:mm')
      return time
    },
    templateHklOptions () {
      return (this.templatesOption || []).map(title => ({
        name: title,
        value: title,
      }))
    },
  },
  watch: {
    schedulesList (val, oldVal) {
      if (val.length > oldVal.length) {
        setTimeout(this.gotoPageBottom, 1)
      }
    },
  },
  methods: {
    ...mapActions('hotb', ['loadHotb', 'updateHotb', 'patchStatus']),
    ...mapMutations('hotb', {
      updateFieldByPath: mutationTypes.HOTB_UPDATE_FIELD_PATH,
      removeSchedule: mutationTypes.HOTB_SCHEDULE_DELETE,
    }),
    async triggerEditStatus () {
      if (!this.isEditing && lodash.isEmpty(this.schedulesList)) {
        await this.add()
      }
      this.isEditing = !this.isEditing
    },
    addOnePhoto (item) {
      let newValue = lodash.cloneDeep(this.editItem.files || [])
      newValue.push(item)
      this.updateFieldByPath({
        path: ['schedules', this.editIndex, 'files'],
        newValue,
      })
    },
    deleteOnePhoto (photo, editItem, editIndex) {
      this.editItem = lodash.cloneDeep(editItem)
      this.editIndex = editIndex
      const index = lodash.findIndex(this.editItem.files, file => file.id === photo.id)
      if (index !== -1) {
        this.editItem.files.splice(index, 1)
        this.updateFieldByPath({
          path: ['schedules', this.editIndex, 'files'],
          newValue: this.editItem.files,
        })
      }
    },
    selectEditPhotoInfo (item, index) {
      if (this.isEditing && this.allowEdit) {
        this.editItem = item
        this.editIndex = index
      }
    },
    deleteOnePhotoInfo (index) {
      this.removeSchedule(index)
    },
    updatePhotoInfo (path, index, val) {
      this.updateFieldByPath({
        path: ['schedules', index, path],
        newValue: val,
      })
    },
    async save () {
      formUtils.resetDirty()
      if (this.validation()) {
        this.triggerEditStatus()
        this.updateFieldByPath({ path: ['signatures'], newValue: this.$refs.signatureRef.signatures })
        await this.updateHotb({attrs: ['schedules', 'remarksList', 'signatures']})
        await this.loadHotb(this.$router.currentRoute.params.id)
      }
    },
    async add () {
      const id = await getKey()
      this.updateFieldByPath({
        path: ['schedules', this.schedulesList.length],
        newValue: lodash.set(lodash.cloneDeep(templateChip), 'id', id),
      })
    },
    async cancel () {
      this.triggerEditStatus()
      await this.loadHotb(this.$router.currentRoute.params.id)
      formUtils.resetDirty()
    },
    gotoPageBottom () {
      const items = $('.inputGroupItem')
      if (!lodash.isEmpty(items)) {
        window.scrollTo(0, items[items.length - 1].offsetTop)
      }
    },
    uploadSuccess (files) {
      if (files.length) {
        this.addOnePhoto(files[0])
      }
    },
    beforeUpload (xhr, file) {
      let isNotUploadedFile = true
      this.editItem.files.map(item => {
        const itemFilename = item.filename.split('|NAME|')[1]
        if (itemFilename === file.name) {
          isNotUploadedFile = false
        }
      })
      if (!isNotUploadedFile) {
        notification.fireNotification(false, `File ${file.name} upload error: duplicate file name`)
      }
      return isNotUploadedFile
    },
    async importTemplate () {
      const templates = lodash.get(this.buildingInfo, 'templates', [])
      if (!lodash.isEmpty(templates)) {
        let templateInfo = {}
        templates.map(item => {
          if (item.title === this.choosedTemplate) {
            templateInfo = item
          }
        })
        const templateDetail = await getBuildingTemplateDetail(this.buildingId, templateInfo.id)
        const keys = arrayHelper.ensureArray(await getKey(templateDetail.items.length))
        templateDetail.items.map((item, index) => {
          item.id = keys[index]
        })
        this.updateFieldByPath({
          path: ['schedules'],
          newValue: this.schedulesList.concat(templateDetail.items),
        })
        this.gotoPageBottom()
        notification.fireNotification(true, 'success')
      }
      this.closeModal()
    },
    dragEnd (list) {
      this.updateFieldByPath({
        path: ['schedules'],
        newValue: list,
      })
    },
    validation () {
      fusionInputCheckUtil.validateAllFields({allowEmpty: 'none'}, '.handover-schedule-edit')
      return $('.handover-schedule-edit input.is-invalid-input').length === 0 &&
        fusionInputCheckUtil.isAllFieldsValid('.handover-schedule-edit')
    },
    resetValidation () {
      fusionInputCheckUtil.resetAllFields()
    },
    async initSchedule () {
      if (Object.keys(this.currentHotb).length && lodash.isEmpty(this.schedulesList)) {
        this.updateFieldByPath({ path: ['schedules'], newValue: [templateChip] })
        await this.updateHotb({ attrs: ['schedules'], notify: false })
        await this.loadHotb(this.$router.currentRoute.params.id)
      }
    },
    async generatePDF () {
      if (!this.remarksListAsIsRadio && this.isAsIs) {
        notification.fireNotification(false, this.$t('handover.specifyAsIsRemarksHint'))
        return false
      }
      if (!this.otherRemarksRadio) {
        notification.fireNotification(false, this.$t('handover.specifyOtherRemarksHint'))
        return false
      }
      this.updateFieldByPath({ path: ['signatures'], newValue: this.$refs.signatureRef.signatures })
      await this.updateHotb({attrs: ['signatures']})
      const remarksListRequest = this.$refs.remarksList.combinateRemarksList()
      const imgUrls = this.$refs.signatureRef.getImgUrl()
      this.pdfId = await exportHotbSchedule(this.currentHotb, remarksListRequest, imgUrls, this.regionName)
    },
    chooseOneTemplate (val) {
      this.choosedTemplate = val
    },
    async openModal () {
      this.buildingId = lodash.get(this.currentHotb, 'tenancy.building.id')
      if (this.buildingId) {
        this.buildingInfo = await getBuildingInfoById(this.buildingId)
        this.templatesOption = lodash.get(this.buildingInfo, 'templates', []).map(item => {
          return item.title
        })
        this.$refs.chooseTemplatesModal.open()
      }
    },
    closeModal () {
      this.$refs.chooseTemplatesModal.close()
    },
  },
}
</script>

<style lang="scss">
.handover-schedule-edit {
  .des-header {
    .condition {
      font-family: Verdana;
      text-align: left;
      font-size: 14px;
      padding: 10px;
      background: #F2F6F8;
      .condition-title {
        font-size: 14px;
        font-weight: bold;
        margin-bottom: 5px;
      }
      i.des {
        width: 100%;
        display: inline-block;
      }
      .des,
      .nav {
        font-size: 14px;
      }
      .nav {
        border-bottom: 1px solid #0C3A58;
        cursor: pointer;
      }
    }
  }
  .stick-btnGroup {
    max-width: 1440px;
    margin: 0 auto;
    position: relative;
    width: 100%;
    top: -138px;
    height: 0px;
  }

  .stick-btnGroup.stickyBtnClass {
    .btnGroup {
      margin-right: 15px;
    }
  }

  .btnGroup {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 15px;
    margin-right: -25px;

    button {
      margin: 0;
      white-space: nowrap;
    }

    .generatePDF {
      width: 140px;
    }

    .submit {
      width: 93px;
      background: #E69500;
    }
  }
  .takeback-schedule {
    & > div {
      position: relative;
    }
    .description {
      margin-left: 30px;
    }
    margin-top: 20px;
    width: 100%;
    .facility, textarea, input {
      width: 100%;
    }
    .facility {
      display: flex;
      span {
        width: 30px;
        height: 40px;
        line-height: 40px;
      }
      .input-field.facility-input {
        flex: 1;
      }
      i.fa-times {
        position: absolute;
        right: -18px;
        top: 10px;
        cursor: pointer;
      }
      i.fa-times:before {
        font-size: 18px;
      }
    }
    .facility-input {
      width: 100%;
      display: inline-block;
      text-align: left;
      font-size: 16px;
      font-weight: bold;
    }
  }
  .empty-schedule {
    text-align: center;
    font-weight: bold;
    height: 100px;
    line-height: 100px;
  }
}

#dialog-import {
  width: 90%;
}
</style>
